import axios from 'axios'
import {Promise} from 'es6-promise'
import _ from 'lodash'
import connections from 'src/config/connections'
import store from '../vuex/store'
import {getDiff, getLogDeeply} from './Helper'
import {couchdbIndexManager} from 'src/services/couchdb'

export default class CouchdbClass {
  constructor ({connection, collection, selector, fields, limit, skip, sort, trash}) {
    this.config = {
      connection: connection || 'default',
      db: connections[connection || 'default'].db,
      collection: collection || null,
      selector: selector || {},
      fields: fields || [],
      limit: limit || 1000000,
      skip: skip || 0,
      sort: sort || [],
      trash: trash || false
    }

    this.transformResponse = {
      toOnlyOneDoc: data => {
        let result = JSON.parse(data)
        return result.docs.length === 0 ? null : result.docs[0]
      },
      toAllDocs: data => {
        let result = JSON.parse(data)
        return result.docs && result.docs.length > 0 ? result.docs : []
      },
      toCount: data => {
        let result = JSON.parse(data)
        return result.docs.length
      }
    }
  }

  runPromise (promise) {
    return new Promise((resolve, reject) => {
      promise.then(res => resolve(res)).catch(err => reject(err))
    })
  }

  withTrash () {
    this.config.trash = true
    return this
  }

  noTrash () {
    this.config.trash = false
    return this
  }

  find (docId) {
    return this.runPromise(
      this.getHttpInstance().get(`/${this.getDb()}/${docId}`)
        .then(response => {
          return response.data
        })
    )
  }

  first () {
    this.config.limit = 1
    return this._find(this.transformResponse.toOnlyOneDoc)
  }

  get () {
    return this._find(this.transformResponse.toAllDocs)
  }

  findMany (keys) {
    // '{"keys":["docId1","docId2","docId3"]}' -X POST http://127.0.0.1:5984/demodb/_all_docs?include_docs=true
    return this.runPromise(
      this.getHttpInstance().post(`/${this.getDb()}/_all_docs?include_docs=true`, {keys})
        .then(response => {
          return response.data.rows.map(item => item.doc)
        })
    )
  }

  count () {
    this.select(['_id'])
    return this._find(this.transformResponse.toCount)
  }

  all () {
    return this.get()
  }

  insert (payload) {
    return this._insert(payload)
  }

  update (docId, payload) {
    return this._update(docId, payload)
  }

  /**
   * @deprecated
   */
  upset (docId, payload) {
    return this._upsert(docId, payload)
  }

  upsert (docId, payload) {
    return this._upsert(docId, payload)
  }

  delete (docId, force) {
    console.log('delete', docId, force)
    return this._delete(docId, force)
  }

  viewReduce (ddoc, view) {
    return this._viewReduce(ddoc, view)
  }

  /**
   * Function do upsert of many documents in 2 requests.
   * One to get old values (Get on view)
   * Another one to post a _bulk_docs with all documents to be update or insert
   * on DB.
   *
   * @params { String or Object } database: Database name or object with all
   *   data
   * @params { String } ddoc: design document name
   * @params { String } view: view name from ddoc
   * @params { Array } keys: List of keys to search
   * @params { Array } payload: Documents data to be upsert
   *
   * @return Promise
   */
  view (ddoc, view, key, options, keys, database) {
    if (typeof ddoc === 'object') {
      database = ddoc.hasOwnProperty('database') ? ddoc.database : ''
      view = ddoc.hasOwnProperty('view') ? ddoc.view : ''
      options = ddoc.hasOwnProperty('options') ? ddoc.options : false
      key = ddoc.hasOwnProperty('key') ? ddoc.key : false
      keys = ddoc.hasOwnProperty('keys') ? ddoc.keys : false
      ddoc = ddoc.hasOwnProperty('ddoc') ? ddoc.ddoc : ''
    }

    let params = {
      queries: []
    }

    if (key || keys) {
      let query = {
        keys: key ? [key] : keys
      }
      for (let index in options) {
        if (index === 'group') {
          query[index] = options[index]
        }
      }
      params.queries.push(query)
    }
    // if (key || keys) {
    //   params.queries.push({keys: key ? [key] : keys})
    // }
    // if (options) {
    //   params.queries.push({...options})
    // }

    return this._view({
      database,
      ddoc,
      view,
      params
    })
  }

  /**
   * Execute a POST request to the view defined
   * @param connection - Connection database config to be used
   * @param ddoc - _design on url request (Required)
   * @param view - _view on url request (Required)
   * @param queries - Array of queries with JSON Objects with parameters
   *   (Partially Required)
   * @returns {*}
   */
  viewQueries ({connection, ddoc, view, queries}) {
    let conn = connection !== undefined ? connection : 'default'
    let quer = queries !== undefined ? queries : [{limit: 1}]
    return this._viewQueries(conn, ddoc, view, {queries: quer})
  }

  _viewQueries (connection, ddoc, view, queries) {
    return new Promise((resolve) => {
      this.connection(connection)
      this.getHttpInstance()
        .post(`/${this.getDb()}/_design/${ddoc}/_view/${view}`, queries)
        .then(res => {
          if (!res || !res.data || !res.data.results) {
            resolve([])
          }
          resolve(res.data.results)
        })
        .catch(() => {
          resolve([])
        })
    })
  }

  createView (ddocName, viewName, view) {
    return this._createView(ddocName, viewName, view)
  }

  createFilter (ddocName, viewName, filter) {
    return this._createFilter(ddocName, viewName, filter)
  }

  getAttachmentUrl (docId, payload) {
    return this._getAttachmentUrl(docId, payload)
  }

  async getAttachmentUrlByReference (referenceId, attachmentName) {
    let url = await this._getAttachmentUrlByReference(referenceId, attachmentName)
    return url
  }

  getAttachment (docId, payload) {
    let promise = new Promise((resolve, reject) => {
      this._getAttachment(docId, payload)
        .then(result => {
          resolve(result)
          this.reset()
        })
        .catch(err => {
          reject(err)
          this.reset()
        })
    })
    return promise
  }

  insertAttachment (docId, payload) {
    let promise = new Promise((resolve, reject) => {
      this._insertAttachment(docId, payload)
        .then(result => {
          resolve(result)
          this.reset()
        })
        .catch(err => {
          reject(err)
          this.reset()
        })
    })
    return promise
  }

  deleteAttachment (docId, payload) {
    let promise = new Promise((resolve, reject) => {
      this._deleteAttachment(docId, payload)
        .then(result => {
          resolve(result)
          this.reset()
        })
        .catch(err => {
          reject(err)
          this.reset()
        })
    })
    return promise
  }

  getFullConnection () {
    const conn = connections[this.config.connection]
    return conn.host + '/' + conn.db
  }

  connection (connection) {
    this.config.connection = connection
    if (connections[this.config.connection] === undefined) {
      throw new Error('Connection dont exists.')
    } else {
      this.config.db = connections[this.config.connection].db
    }
    return this
  }

  db (db) {
    this.config.db = db
    return this
  }

  collection (collection) {
    this.config.collection = collection
    return this
  }

  where (field, value) {
    this.config.selector[field] = value
    return this
  }

  whereGreatThen (field, value) {
    this.config.selector[field] = {
      $gt: value
    }
    return this
  }

  whereOr (field, value) {
    if (!this.config.selector.hasOwnProperty('$or')) {
      this.config.selector['$or'] = []
    }

    this.config.selector['$or'] = value.map(item => {
      let a = {}
      a[field] = item
      return a
    })
    return this
  }

  select (fields) {
    this.config.fields = fields
    return this
  }

  limit (limit) {
    this.config.limit = limit
    return this
  }

  skip (skip) {
    this.config.skip = skip
    return this
  }

  sort (sort) {
    if (sort.field !== null) {
      let sortObject = {}
      let mapDirections = {
        ascending: 'asc',
        asc: 'asc',
        descending: 'desc',
        desc: 'desc'
      }
      sortObject[sort.field] = mapDirections[sort.direction]
      this.config.sort = [sortObject]
    }
    return this
  }

  search (search) {
    if (search.query) {
      let conditions = []
      search.fields.map((item) => {
        let condition = {}
        condition[item] = {'$regex': `(?i)${search.query}`}
        conditions.push(condition)
      })
      this.config.selector['$or'] = conditions
    }
    return this
  }

  whereIn (field, inArray) {
    this.config.selector[field] = {'$in': inArray}
    return this
  }

  createIndex (fields, indexName) {
    return this._createIndex(fields, indexName)
  }

  async _find (transformResponse) {
    const query = this.getQuery()
    const db = this.getDb()

    if (query.hasOwnProperty('sort') || query.hasOwnProperty('selector')) {
      await Promise.all(
        couchdbIndexManager.create(query).filter(item => item.fields.length > 0 && item.name.length > 0)
          .map(item => this.createIndex(item.fields, item.name))
      )
    }

    let promises = []
    if (query.hasOwnProperty('sort')) {
      const selector = query.sort.reduce((accumulated, current) => {
        for (let sortField in current) {
          accumulated[sortField] = sortField
        }
        return accumulated
      }, {})

      if (selector.hasOwnProperty('selector') || selector.hasOwnProperty('sort')) {
        await Promise.all(
          couchdbIndexManager.create(selector).filter(item => item.fields.length > 0 && item.name.length > 0)
            .map(item => this.createIndex(item.fields, item.name))
        )
      }
    }
    try {
      await Promise.all(promises)
    } catch (e) {
      console.log(e)
    }
    return this.getHttpInstance()
      .post(`/${db}/_find`, query, {transformResponse: [transformResponse]})
      .then((response) => {
        this.reset()
        return response.data
      })
      .catch((err) => {
        console.log(err)
      })
  }

  _insert (payload, database) {
    let promise = new Promise((resolve, reject) => {
      payload.collection = payload.collection || this.config.collection
      payload.createdAt = new Date()
      payload.updatedAt = new Date()
      if (database) {
        this.connection(database)
      }
      this.getHttpInstance().post(`/${this.getDb()}/`, payload)
        .then((response) => {
          resolve(response.data)
        })
        .catch((err) => reject(err))
    })
    this.reset()
    return promise
  }

  _postBulkDocs (docs) {
    return new Promise((resolve, reject) => {
      this.getHttpInstance().post(`/${this.getDb()}/` + '_bulk_docs', {docs: docs})
        .then((response) => {
          this.reset()
          resolve(response.data)
        })
        .catch((err) => reject(err))
    })
  }

  /**
   * Send only updated filesService
   * @param {Array} docs
   */
  _updateBulkDocs (docs) {
    return this._postBulkDocs(docs.filter(item => {
      return item.hasOwnProperty('_id') && item.hasOwnProperty('_rev')
    }))
  }

  /**
   * Send filesService to upsert on databse.
   * @param {Array} docs
   * @returns promise
   */
  _insertBulk (docs) {
    return this._postBulkDocs(docs)
  }

  /**
   * This method is a mediator to insert logs on database.
   * It get the documents needed and set the config to save in the corrent
   * local.
   * @param {Array} logs Array with objects. Ex: [{ new: {}, old: {} }, { new:
   *   {}, old: {} }]
   * @returns promise
   */
  _insertBuckLogs (logs) {
    const CouchLogs = new CouchdbClass({connection: 'logs', collection: 'logs'})
    return CouchLogs._insertBulk(CouchLogs._getDocsLogs(logs))
  }

  /**
   * This method searchs in the array all updated fields between new and old
   * version of the same document._id
   * @param {Array} logs Array with objects. Ex: [{ new: {}, old: {} }, { new:
   *   {}, old: {} }]
   * @returns array with fields "flatterned". Ex: [ { field: 'cons', to: 3,
   *   from: 2 }, { field: 'user.name', to: 'Nyx', from: 'Athenas' }]
   */
  _getDocsLogs (logs) {
    return logs.map(item => {
      let diff = getDiff(item.new, item.old)
      let typeOfChange = ['updated']
      let ignoreFields = ['updatedAt', '_id', '_rev']
      let docLogs = getLogDeeply(diff, typeOfChange, ignoreFields).map(itemLog => {
        itemLog.user = store.state.user._id
        itemLog.userName = store.state.user.name
        itemLog.referenceId = item.old._id
        itemLog.collection = item.old.collection
        itemLog.createdAt = new Date()
        return itemLog
      })
      return docLogs
    }).reduce((accum, current, index, complete) => {
      if (complete.length > 0) {
        if (index === 0) {
          accum = []
        }
        return [...accum, ...current]
      }
    }, [])
  }

  _update (docId, payload, database) {
    let promise = new Promise((resolve, reject) => {
      if (database) {
        this.connection(database)
      }

      this.find(docId)
        .then(oldDoc => {
          if (oldDoc === null) {
            reject(new Error('Document not found.'))
            return
          }
          payload.updatedAt = new Date()
          payload._rev = oldDoc._rev

          if (database) {
            this.connection(database)
          }

          this.getHttpInstance()
            .put(`/${this.getDb()}/${docId}`, payload)
            .then((response) => {
              if (database) {
                this.connection(database)
              }
              this._insertBuckLogs([
                {
                  new: payload,
                  old: oldDoc
                }
              ])
              resolve(response.data)
            })
            .catch((err) => reject(err))
        })
    })
    this.reset()
    return promise
  }

  /**
   * Function do upsert of many documents in 2 requests.
   * One to get old values (Get on view)
   * Another one to post a _bulk_docs with all documents to be update or insert
   * on DB.
   *
   * @params { String } database: Database name
   * @params { String } ddoc: design document name
   * @params { String } view: view name from ddoc
   * @params { Array } payload: Documents data to be upsert. OBS: need have _id
   *   on each data
   *
   * @return Promise
   */
  upsertManyWithView ({database, ddoc, view, payload}) {
    return new Promise(resolve => {
      let keys = payload.filter(item => item._id).map(item => item._id)
      if (keys.length <= 0) {
        resolve(false)
      }

      this.view({database, ddoc, view, keys: keys})
        .then(res => {
          if (!res.rows) {
            return
          }

          let currentDocsOnDb = res.rows.map(item => item.value)
            .reduce((acc, curr, indx) => {
              if (indx === 0) {
                acc = {}
              }
              acc[curr._id] = curr
              return acc
            }, {})

          let logs = []
          let bulkDocs = payload.map(item => {
            let doc = Object.assign({}, this.addDefaultData(item))
            if (currentDocsOnDb.hasOwnProperty(item._id)) {
              doc = Object.assign({}, currentDocsOnDb[item._id], item)
              doc._rev = currentDocsOnDb[item._id]._rev
              doc.updatedAt = new Date()
              logs.push({
                old: currentDocsOnDb[item._id],
                new: doc
              })
            }
            return doc
          })

          Promise.all([this._insertBulk(bulkDocs), this._insertBuckLogs(logs)])
            .then(res => {
              resolve(res)
            })
        })
        .catch(err => {
          resolve(false)
          console.log(err)
        })
    })
  }

  upsertWithView ({database, ddoc, view, payload}) {
    let promise = new Promise((resolve, reject) => {
      if (database) {
        this.connection(database)
      }
      payload = Object.assign({}, this.addDefaultData(payload))

      if (!payload.hasOwnProperty('_id') || !payload._id) {
        this.getHttpInstance().post(`/${this.getDb()}/` + '_bulk_docs', {docs: [payload]})
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => reject(err))
        return
      }

      this.view({ddoc, view, key: payload._id})
        .then(response => {
          let oldDoc = response.rows[0].value
          payload.updatedAt = new Date()
          payload._rev = oldDoc._rev
          if (database) {
            this.connection(database)
          }

          this.getHttpInstance()
            .put(`/${this.getDb()}/${payload._id}`, payload)
            .then((response) => {
              if (database) {
                this.connection(database)
              }

              this._insertBuckLogs([
                {
                  new: payload,
                  old: oldDoc
                }
              ])
              resolve(response.data)
            })
            .catch((err) => reject(err))
        })
    })

    this.reset()
    return promise
  }

  updateWithView ({database, ddoc, view, payload}) {
    let promise = new Promise((resolve, reject) => {
      if (database) {
        this.connection(database)
      }

      payload = Object.assign({}, this.addDefaultData(payload))

      this.view({ddoc, view, key: payload._id})
        .then(response => {
          if (!response.rows[0] && !response.rows[0].value) {
            resolve(null)
          }

          let oldDoc = response.rows[0].value
          payload.updatedAt = new Date()
          payload._rev = oldDoc._rev
          if (database) {
            this.connection(database)
          }

          this.getHttpInstance()
            .put(`/${this.getDb()}/${payload._id}`, payload)
            .then((response) => {
              if (database) {
                this.connection(database)
              }

              this._insertBuckLogs([
                {
                  new: payload,
                  old: oldDoc
                }
              ])
              resolve(response.data)
            })
            .catch((err) => reject(err))
        })
    })
    this.reset()
    return promise
  }

  _upsert (docId, payload) {
    let promise = new Promise(async (resolve, reject) => {
      if (docId) {
        try {
          const result = await this._head(docId)
          if (result) {
            return this._update(docId, payload)
              .then(result => resolve(result))
              .catch(err => reject(err))
          }
        } catch (e) {
          console.log(e)
        }
      }
      return this._insert(payload)
        .then(result => resolve(result))
        .catch(err => reject(err))
    })
    return promise
  }

  _delete (docId, force) {
    console.log('_delete', docId, force)
    if (!force) {
      return this.find(docId)
        .then(doc => {
          doc.deletedAt = new Date()
          this.upsert(docId, doc)
        })
    }
    return new Promise((resolve, reject) => {
      this._head(docId)
        .then(result => {
          let rev = result.headers.etag.replace(/"/g, '')
          this.getHttpInstance()
            .delete(`/${this.getDb()}/${docId}`, {params: {'rev': rev}})
            .then((response) => {
              this.reset()
              resolve(response.data)
            })
            .catch((err) => {
              this.reset()
              reject(err)
            })
        })
        .catch(err => {
          this.reset()
          reject(err)
        })
    })
  }

  _head (docId) {
    return this.getHttpInstance().head(`/${this.config.db}/${docId}`)
  }

  _getRev (docId, DB) {
    if (!DB) {
      DB = this.getDb()
    }

    return new Promise((resolve, reject) => {
      this._head(docId, DB)
        .then(result => {
          resolve(result.headers.etag.replace(/"/g, ''))
        })
        .catch((err) => {
          reject(err)
        })
    })
  }

  /**
   * @param { String } database Database Name
   * @param { String } ddoc Design Document Name
   * @param { String } view View Name
   * @param { String } view Key
   * @param { Object } options
   *
   * @return { Promise }
   */
  _view ({database, ddoc, view, params}) {
    if (database && database !== '') {
      this.connection(database)
    }

    let query = params.queries.length > 0 ? this.getHttpInstance().post(`/${this.getDb()}/_design/${ddoc}/_view/${view}`, params) : this.getHttpInstance().get(`/${this.getDb()}/_design/${ddoc}/_view/${view}`)

    let promise = new Promise((resolve, reject) => {
      query.then((response) => {
        if (params.queries.length > 0) {
          resolve(response.data.results[0])
        } else {
          resolve(response.data)
        }
      }).catch((err, response) => {
        reject(err)
      })
    })
    this.reset()
    return promise
  }

  viewBetween (ddoc, view, keys, options) {
    if (typeof ddoc === 'object') {
      view = ddoc.hasOwnProperty('view') ? ddoc.view : ''
      keys = ddoc.hasOwnProperty('keys') ? ddoc.keys : ''
      options = ddoc.hasOwnProperty('options') ? ddoc.options : {}
      ddoc = ddoc.hasOwnProperty('ddoc') ? ddoc.ddoc : ''
    }
    return this._viewBetween(ddoc, view, keys, options)
  }

  _viewBetween (ddoc, view, keys, options) {
    let promise = new Promise((resolve, reject) => {
      let getparams = ''
      if (options && Object.keys(options).length > 0) {
        for (let param in options) {
          getparams += getparams === '' ? `?${param}=${options[param]}` : `&${param}=${options[param]}`
        }
      }
      if (typeof keys !== 'undefined' && keys.hasOwnProperty('startKey') && keys.hasOwnProperty('endKey')) {
        getparams += getparams === '' ? '?' : '&'

        if (keys.startIndex !== undefined && keys.startIndex >= 0 && keys.startIndex < keys.endIndex) {
          getparams += `start_key=["${keys.startKey}", ${keys.startIndex}]&end_key=["${keys.endKey}", ${keys.endIndex}]`
        } else {
          getparams += `start_key="${keys.startKey}"`
        }

        if (keys.hasOwnProperty('endIndex') && keys.endIndex !== undefined) {
          getparams += `&end_key=["${keys.endKey}", ${keys.endIndex}]`
        } else {
          getparams += `&end_key="${keys.endKey}"`
        }
      }
      this.getHttpInstance().get(`/${this.getDb()}/_design/${ddoc}/_view/${view}${getparams}`)
        .then((response) => {
          resolve(response.data)
        })
        .catch((err, response) => {
          reject(err)
        })
    })
    this.reset()
    return promise
  }

  _viewReduce (ddoc, view) {
    // _design/counters/_view/surveys-by-status?group=true&limit=101&reduce=true&skip=0
    let promise = new Promise((resolve, reject) => {
      this.getHttpInstance().get(`/${this.getDb()}/_design/${ddoc}/_view/${view}?group=true&reduce=true&skip=0`)
        .then((response) => {
          resolve(response.data)
        })
        .catch((err, response) => {
          reject(err)
        })
    })
    this.reset()
    return promise
  }

  /**
   *
   * @param { String } ddoc Design Document Name
   * @param { String } viewName View Name
   * @param { String } view View Content
   */
  _createView (ddocName, viewName, view) {
    let ddocUri = `/${this.getDb()}/_design/${ddocName}`
    let promise = new Promise((resolve, reject) => {
      this._getDesignDocument(ddocName)
        .then(ddoc => {
          let oldDdoc = _.cloneDeep(ddoc)
          let newDdoc
          let views = {views: {}}
          views['views'][viewName] = view
          if (ddoc === undefined) {
            newDdoc = views
          } else {
            newDdoc = _.merge(ddoc, views)
            newDdoc._rev = ddoc._rev
          }
          if (!_.isEqual(oldDdoc, newDdoc)) {
            this.getHttpInstance().put(ddocUri, newDdoc)
              .then((response) => {
                resolve(response.data)
              })
              .catch(err => reject(err))
          } else {
            resolve(true)
          }
        })
        .catch(err => reject(err))
    })
    return promise
  }

  _createFilter (ddocName, filterName, filter) {
    let ddocUri = `/${this.getDb()}/_design/${ddocName}`
    let promise = new Promise((resolve, reject) => {
      this._getDesignDocument(ddocName)
        .then(ddoc => {
          let oldDdoc = _.cloneDeep(ddoc)
          let newDdoc
          let filters = {filters: {}}
          filters['filters'][filterName] = filter
          if (ddoc === undefined) {
            newDdoc = filters
          } else {
            newDdoc = _.merge(ddoc, filters)
          }

          if (!_.isEqual(oldDdoc, newDdoc)) {
            this.getHttpInstance().put(ddocUri, newDdoc)
              .then((response) => {
                resolve(response.data)
              })
              .catch(err => reject(err))
          } else {
            resolve(true)
          }
        })
        .catch(err => reject(err))
    })
    return promise
  }

  createFilters (designName, filters) {
    let ddocUri = `/${this.getDb()}/_design/${designName}`
    return this._getDesignDocument(designName)
      .then(old => {
        let document = Object.assign({filters: []}, old)
        document['filters'] = filters

        if (old && old.hasOwnProperty('_rev')) {
          this.getHttpInstance().put(ddocUri, document)
            .then((response) => {
              return response.data
            })
        } else {
          this.getHttpInstance()
            .post(
              `/${this.getDb()}`,
              Object.assign({_id: `_design/${designName}`}, document)
            )
            .then((response) => {
              return response.data
            })
        }
      }).catch(err => console.log(err))
  }

  _getDesignDocument (ddocName) {
    return new Promise((resolve, reject) => {
      let ddocUri = `/${this.getDb()}/_design/${ddocName}`
      this.getHttpInstance().get(ddocUri)
        .then((response) => {
          resolve(response.data)
        })
        .catch(err => {
          if (err.response.status === 404) {
            resolve(undefined)
          } else {
            reject(err)
          }
        })
    })
  }

  /**
   * @param { Array } fields
   * @param { String } indexName
   */
  _createIndex (fields, indexName) {
    let promise = new Promise((resolve, reject) => {
      this.getHttpInstance()
        .post(`/${this.getDb()}/_index`, {index: {fields: fields}, name: indexName, type: 'json'})
        .then((response) => {
          resolve(response)
        })
        .catch((err, response) => {
          reject(err)
        })
        .finally(() => {
          this.reset()
        })
    })
    return promise
  }

  _bulkGet (docIds) {
    return new Promise((resolve, reject) => {
      const DB = this.getDb()
      this.getHttpInstance().post(
        `/${DB}/_bulk_get/`,
        {docs: docIds}
      )
        .then((response) => {
          resolve(response.data.results.filter(item => item.docs[0].hasOwnProperty('ok'))
            .map(item => item.docs[0].ok))
        })
        .catch((err) => reject(err))
    })
  }

  /**
   *
   * @param { String } docId
   * @param { Object } payload
   * {
   *   mimeType: 'image/png',
   *   attachmentName: 'perfil.jpg',
   *   attachment: Binary File
   * }
   */
  _insertAttachment (docId, payload) {
    return new Promise((resolve, reject) => {
      const DB = this.getDb()
      this._getRev(docId, DB)
        .then(rev => {
          // let attachmentName = encodeURI(payload.attachmentName)
          this.getHttpInstance()
            .put(`/${DB}/${docId}/${payload.attachmentName}`, payload.attachment, {
              headers: {'Content-Type': payload.mimeType},
              params: {rev: rev}
            })
            .then((response) => {
              resolve(response.data)
            })
            .catch((err) => reject(err))
        })
        .catch(err => reject(err))
    })
  }

  /**
   *
   * @param { String } docId
   * @param { Object } payload
   * {
   *   mimeType: 'image/png',
   *   attachmentName: 'perfil.jpg'
   * }
   */
  _deleteAttachment (docId, payload) {
    return new Promise((resolve, reject) => {
      const DB = this.getDb()
      this._getRev(docId)
        .then(rev => {
          let attachmentName = encodeURI(payload.attachmentName)
          this.getHttpInstance().delete(`/${DB}/${docId}/${attachmentName}`, {params: {rev: rev}})
            .then((response) => {
              resolve(response.data)
            })
            .catch((err) => reject(err))
        })
        .catch(err => reject(err))
    })
  }

  /**
   *
   * @param { String } docId
   * @param { String } attachmentName
   */
  _getAttachment (docId, attachmentName) {
    let attachmentUrl = this._getAttachmentUrl(docId, attachmentName)
    return new Promise((resolve, reject) => {
      this.getHttpInstance().get(attachmentUrl, {responseType: 'blob'})
        .then((response) => {
          resolve(response.data)
        })
        .catch((err) => reject(err))
    })
  }

  /**
   *
   * @param { String } docId
   * @param { String } attachmentName
   */
  _getAttachmentUrl (docId, attachmentName) {
    let connection = connections[this.config.connection]
    this.config.db = connection.db
    let url = `${connection.host}/${this.getDb()}/${docId}/${attachmentName}`
    this.reset()
    return url
  }

  /**
   *
   * @param { String } docId
   * @param { String } attachmentName
   */
  _getAttachmentUrlByReference (referenceId, attachmentName) {
    let promise = new Promise((resolve, reject) => {
      let connection = connections[this.config.connection]
      this.config.db = connection.db
      this.where('referenceId', referenceId)
      this.getHttpInstance().post(`/${this.getDb()}/_find`, this.getQuery(), {transformResponse: this.transformResponse.toOnlyOneDoc})
        .then((response) => {
          let doc = response.data
          resolve(`${connection.host}/${connection.db}/${doc._id}/${attachmentName}`)
        })
        .catch((err) => reject(err))
    })
    this.reset()
    return promise
  }

  getDb () {
    // console.log('CONFIG', this.config, this.config.db, this.config.connection)
    return this.config.db
  }

  getQuery () {
    // console.log('couchdbclass', this.config)
    if (this.config.collection !== null && (!this.config.selector.collection || this.config.selector.collection === '')) {
      this.config.selector.collection = this.config.collection
    }
    let query = {
      'selector': {}
    }
    if (this.config.selector && Object.keys(this.config.selector).length > 0) {
      query['selector'] = this.config.selector
    }

    if (this.config.limit) {
      query['limit'] = this.config.limit
    } else {
      delete query['limit']
    }

    if (this.config.skip) {
      query['skip'] = this.config.skip
    } else {
      delete query['skip']
    }

    if (this.config.fields.length > 0) {
      query['fields'] = this.config.fields
    }

    if (this.config.sort && this.config.sort.length > 0) {
      query['sort'] = this.config.sort
      for (let sort of query['sort']) {
        for (let field in sort) {
          if (!query['selector'].hasOwnProperty(field)) {
            query['selector'][field] = {$exists: true}
          }
        }
      }
    } else {
      delete query['sort']
    }

    if (!query.hasOwnProperty('fields') || query['fields'].length === 0) {
      delete query['fields']
    }

    if (Object.values(query.selector).length === 0) {
      query.selector = {
        _id: {
          '$gt': 0
        }
      }
    }

    if (!this.config.trash) {
      query.selector['deletedAt'] = {
        '$exists': false
      }
    }

    return query
  }

  getHttpInstance () {
    let connection = connections[this.config.connection]
    if (connection === undefined) {
      throw new Error('Connection dont exists.')
    }
    this.connection(this.config.connection)

    return axios.create({
      baseURL: connection.host,
      timeout: 9999999,
      auth: {
        username: connection.user,
        password: connection.pass
      }
    })
  }

  reset () {
    this.config = Object.assign(this.config, {
      selector: {},
      fields: [],
      limit: 25,
      skip: 0,
      sort: [],
      trash: false
    })
    this.config.fields = JSON.parse(JSON.stringify([]))
    this.config.sort = JSON.parse(JSON.stringify([]))
  }

  addDefaultData (obj) {
    // console.log(obj)
    if (!obj.hasOwnProperty('collection')) {
      obj.collection = this.config.collection
    }

    if (!obj.hasOwnProperty('createdAt')) {
      obj.createdAt = new Date()
    }

    if (!obj.hasOwnProperty('updatedAt')) {
      obj.updatedAt = new Date()
    }

    if (!obj.hasOwnProperty('userId') && store.state.hasOwnProperty('user')) {
      obj.userId = store.state.user._id
    }

    if (!obj.hasOwnProperty('servername') && store.state.hasOwnProperty('config')) {
      obj.servername = store.state.config.hasOwnProperty('servername') ? store.state.config.servername : '3EENG-unamed'
    }
    return Object.assign({}, obj)
  }

  deleteDesignView (designDoc) {
    return new Promise((resolve, reject) => {
      this._getDesignDocument(designDoc)
        .then(ddoc => {
          this.getHttpInstance().delete(`/${this.getDb()}/_design/${designDoc}?rev=${ddoc._rev}`)
            .then((response) => {
              resolve(response.data)
            })
            .catch(err => reject(err))
        })
    })
  }
}



// WEBPACK FOOTER //
// ./src/services/CouchdbClass.js